import { Borders, Row, Workbook, Worksheet } from "exceljs"
import moment from "moment"
import { CardType } from "../../components/TabByCard/types"
import { APPROVAL, DELIVERED } from "../vars"

type ExportExcelByCard = {
  type: "REGISTER BY CARD" | "REGISTER BY LOCATION" | "REFERENCE ID"
  data: CardType[]
  dateFilter: string
  companyName?: string
}

export const exportExcelByCard = async ({
  type,
  data,
  dateFilter
}: ExportExcelByCard) => {
  const isByCard = type === "REGISTER BY CARD"
  const isByLocation = type === "REGISTER BY LOCATION"
  const workbook = new Workbook()
  const worksheet = workbook.addWorksheet("Sheet 1")
  const columns = [
    "NO",
    "SO ID#",
    "DATE",
    isByCard ? "CARD" : "CUSTOMER",
    isByCard ? "TRADER" : isByLocation ? "LOCATION" : "REFERENCE",
    "APPROVAL",
    "DELIVERED",
    "QTY ORDER",
    "QTY DELIVERED",
    "QTY BACK ORDER",
    "TOTAL SALES",
    "TOTAL PAYMENT",
    "BALANCE OWING"
  ]
  worksheet.views = [{ showGridLines: false }]

  // Title
  makeTitle(worksheet, dateFilter, type)

  // Header
  const headers = worksheet.addRow(columns)
  headers.font = { bold: true, color: { argb: 'FFFFFFFF' } }
  headers.eachCell((cell) => {
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF000000" }
    }
    cell.border = {
      top: { style: "thin", color: { argb: "000000" } },
      left: { style: "thin", color: { argb: "000000" } },
      bottom: { style: "thin", color: { argb: "000000" } },
      right: { style: "thin", color: { argb: "000000" } }
    }
  })

  makeRows(isByCard, data, worksheet)

  // create xlsx
  await workbook.xlsx.writeBuffer().then((buffer) => {
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    })
    const name = isByCard ? "register-by-card" : isByLocation ? "register-by-location" : "reference"
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `${name}-${dateFilter}.xlsx`
    a.click()
    URL.revokeObjectURL(url)
  })
}

const makeTitle = (worksheet: Worksheet, dateFilter: string, type: string) => {
  worksheet.mergeCells(1, 1, 1, 13)
  const purchaseCell = worksheet.getCell(1, 1)
  purchaseCell.value = "SALES"
  purchaseCell.font = { bold: true, size: 15 }
  purchaseCell.alignment = {
    vertical: "middle",
    horizontal: "center"
  }

  worksheet.mergeCells(2, 1, 2, 13)
  const registerCell = worksheet.getCell(2, 1)
  registerCell.value = type
  registerCell.font = { bold: true, size: 15 }
  registerCell.alignment = {
    vertical: "middle",
    horizontal: "center",
    wrapText: true
  }

  worksheet.mergeCells(3, 1, 3, 13)
  const dateCell = worksheet.getCell(3, 1)
  dateCell.value = `DATE: ${dateFilter}`
  dateCell.font = { bold: true, size: 13 }
  dateCell.alignment = {
    vertical: "middle",
    horizontal: "left",
    wrapText: true
  }

  worksheet.getColumn(2).width = 20
}

function makeRows(
  isByCard: boolean,
  data: ExportExcelByCard["data"],
  worksheet: Worksheet
) {
  let itemRow: Row

  const cellBorder: Partial<Borders> = {
    top: { style: "thin", color: { argb: "000000" } },
    left: { style: "thin", color: { argb: "000000" } },
    bottom: { style: "thin", color: { argb: "000000" } },
    right: { style: "thin", color: { argb: "000000" } }
  }

  data.forEach((item, index) => {
    itemRow = worksheet.addRow([
      index + 1,
      item.trx_code,
      moment(item.transaction_date).format("DD/MM/YYYY"),
      isByCard ? "CUSTOMER" : item.customer.customer_name,
      isByCard ? item.customer.customer_name : item.location.location_name,
      APPROVAL[item.approval_status],
      DELIVERED[item.delivery_status],
      item.qty_order,
      item.qty_delivered,
      item.qty_back_order,
      item.total_sales,
      item.total_payment,
      item.balance_owing
    ])

    itemRow.eachCell((cell) => {
      cell.border = cellBorder
    })
  })

  const calculate = data.reduce(
    (acc, item) => {
      return {
        balance: acc.balance + item.balance_owing,
        payment: acc.payment + item.total_payment
      }
    },
    { balance: 0, payment: 0 }
  )

  // summary
  const sumarryPayment = worksheet.addRow(
    Array(11).fill("").concat(["SUMMARY PAYMENT", calculate.payment])
  )

  const sumarryBalance = worksheet.addRow(
    Array(11).fill("").concat(["SUMMARY BALANCE", calculate.balance])
  )

  sumarryPayment.getCell(12).font = { bold: true }
  sumarryPayment.getCell(13).font = { bold: true }
  sumarryBalance.getCell(12).font = { bold: true }
  sumarryBalance.getCell(13).font = { bold: true }
}
